import { Link, createFileRoute } from '@tanstack/react-router';
import { StatusChip } from '@/components/migration/file-drop';
import { EmptyState, PageHeader } from '@/components/page-header';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDate } from '@/lib/format';
import { fmtAmount } from '@/lib/migration';
import { useOrgId } from '@/lib/org';
import { useImportedTransactions, type ImportedTxnView } from '@/lib/qbo-history';

export const Route = createFileRoute('/settings/migration/history/$runId')({
  component: ImportedTransactions,
});

const OUTCOME: Record<ImportedTxnView['outcome'], string> = {
  document: 'Document',
  payment: 'Payment',
  journal: 'Journal entry',
  applied: 'Credits applied',
  skipped: 'Skipped',
};

/** Screen G13 (document 06): what one QuickBooks import created, each linked to the record. */
function ImportedTransactions() {
  const { runId } = Route.useParams();
  const orgId = useOrgId();
  const q = useImportedTransactions(runId);
  return (
    <>
      <PageHeader
        title="Imported from QuickBooks"
        description="Each QuickBooks transaction of this import and what it became in Yedo Books."
      />
      {!orgId ? (
        <EmptyState title="Select a client" hint="Use the client switcher in the top bar." />
      ) : q.isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : q.isError ? (
        <EmptyState title="Could not load the transactions" hint={q.error.message} />
      ) : (
        <Table className="max-w-6xl" data-testid="qbo-imported-transactions">
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>QuickBooks</TableHead>
              <TableHead>Became</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Note</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {q.data.map((x) => (
              <TableRow key={`${x.type}:${x.qboId}`}>
                <TableCell className="tnum">{formatDate(x.date)}</TableCell>
                <TableCell>
                  {x.type} {x.number ?? `#${x.qboId}`}
                </TableCell>
                <TableCell>
                  <Target x={x} />
                </TableCell>
                <TableCell className="tnum text-right">
                  {x.amount !== null ? `${x.currency ?? ''} ${fmtAmount(x.amount)}` : ''}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">{x.note ?? ''}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </>
  );
}

function Target({ x }: { x: ImportedTxnView }) {
  const label = OUTCOME[x.outcome];
  if (x.outcome === 'skipped') return <StatusChip tone="neutral">{label}</StatusChip>;
  const cls = 'underline';
  if (x.documentId) {
    const p = { documentId: x.documentId };
    switch (x.documentKind) {
      case 'invoice':
        return (
          <Link className={cls} to="/sales/invoices/$documentId" params={p}>
            Invoice
          </Link>
        );
      case 'credit_note':
        return (
          <Link className={cls} to="/sales/credit-notes/$documentId" params={p}>
            Credit note
          </Link>
        );
      case 'sales_receipt':
        return (
          <Link className={cls} to="/sales/sales-receipts/$documentId" params={p}>
            Sales receipt
          </Link>
        );
      case 'quote':
        return (
          <Link className={cls} to="/sales/quotes/$documentId" params={p}>
            Quote
          </Link>
        );
      case 'bill':
        return (
          <Link className={cls} to="/expenses/bills/$documentId" params={p}>
            Bill
          </Link>
        );
      case 'debit_note':
        return (
          <Link className={cls} to="/expenses/debit-notes/$documentId" params={p}>
            Debit note
          </Link>
        );
      case 'expense':
        return (
          <Link className={cls} to="/expenses/expenses/$documentId" params={p}>
            Expense
          </Link>
        );
    }
  }
  if (x.paymentId)
    return x.type === 'BillPayment' ? (
      <Link className={cls} to="/expenses/payments/$paymentId" params={{ paymentId: x.paymentId }}>
        Bill payment
      </Link>
    ) : (
      <Link className={cls} to="/sales/payments/$paymentId" params={{ paymentId: x.paymentId }}>
        Payment
      </Link>
    );
  if (x.journalEntryId)
    return (
      <Link
        className={cls}
        to="/accounting/journal-entries/$entryId"
        params={{ entryId: x.journalEntryId }}
      >
        Journal entry
      </Link>
    );
  return <span>{label}</span>;
}
